import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useState, useEffect, useRef } from "react";
import { Dropdown } from "flowbite-react";
import { IoClose } from "react-icons/io5";
import { FaCaretDown } from "react-icons/fa6";
import { trackApplyButton } from "@/utils/dataLayer";
import logo from "@/images/logo-dark.png";
import AzeFlag from "@/images/aze-flag.svg";
import EngFlag from "@/images/en-flag.svg";

export default function Drawer({ isOpen, setIsOpen, scrollToSection }) {
  const router = useRouter();
  const { t } = useTranslation();
  const [academyOpen, setAcademyOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeChildLink, setActiveChildLink] = useState(null);
  const [showHeader, setShowHeader] = useState(true);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;
    const { style } = document.body;
    const previousOverflow = style.overflow;
    const previousPosition = style.position;
    const previousTop = style.top;
    const previousWidth = style.width;

    style.overflow = "hidden";
    style.position = "fixed";
    style.top = `-${scrollY}px`;
    style.width = "100%";

    return () => {
      style.overflow = previousOverflow;
      style.position = previousPosition;
      style.top = previousTop;
      style.width = previousWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    const programSlug = router.query.program;
    if (router.pathname === "/programs" && !programSlug) {
      setActiveDropdown("programs");
      setActiveChildLink("programs");
    } else if (programSlug === "front-end") {
      setActiveDropdown("programs");
      setActiveChildLink("programs-frontend");
    } else if (programSlug === "back-end") {
      setActiveDropdown("programs");
      setActiveChildLink("programs-backend");
    } else if (programSlug === "full-stack") {
      setActiveDropdown("programs");
      setActiveChildLink("programs-fullstack");
    } else if (router.pathname === "/about-us") {
      setActiveDropdown("academy");
      setActiveChildLink("about-us");
    } else if (router.pathname === "/" && router.asPath.includes("#methodology")) {
      setActiveDropdown("academy");
      setActiveChildLink("methodology");
    } else if (router.pathname === "/branches") {
      setActiveDropdown("academy");
      setActiveChildLink("branches");
    } else {
      setActiveDropdown(null);
      setActiveChildLink(null);
    }
  }, [router.pathname, router.asPath, router.query]);

  const isBlogPage = router.pathname === '/blogs' || router.pathname.startsWith('/blogs/');

  const changeLanguage = (locale) => async () => {
    if (isBlogPage) {
      const query = { ...router.query, lang: locale };
      await router.push({ pathname: router.pathname, query }, undefined, { locale });
    } else {
      await router.push({ pathname: router.route, query: router.query }, router.asPath, { locale });
    }
    setIsOpen(false);
  };

  const handleLinkClick = (sectionId) => async (e) => {
    e.preventDefault();
    setActiveDropdown("academy");
    setActiveChildLink(sectionId);
    setIsOpen(false);
    if (router.pathname === "/") {
      scrollToSection(sectionId, "center");
    } else {
      await router.push("/").then(() => setTimeout(() => scrollToSection(sectionId, "center"), 500));
    }
  };

  const theme = {
    arrowIcon: "hidden",
    floating: {
      base: "z-10 w-fit divide-y divide-gray-100 rounded shadow",
      item: { base: "flex w-full cursor-pointer items-center justify-start px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" },
      style: { auto: "border border-gray-200 bg-white text-gray-900" },
    },
    inlineWrapper: "flex items-center group",
  };

  const handleDrawerScroll = (e) => {
    const currentScrollTop = e.currentTarget.scrollTop;

    if (currentScrollTop <= 10) {
      setShowHeader(true);
    } else {
      setShowHeader(currentScrollTop < lastScrollTopRef.current);
    }

    lastScrollTopRef.current = currentScrollTop;
  };

  return (
    <main
      className={`fixed inset-0 z-[110] overflow-hidden overscroll-none bg-gray-900 bg-opacity-25 transition-opacity duration-500 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
    >
      <section
        className={`fixed top-0 right-0 h-full w-screen max-w-[100vw] transform bg-white shadow-xl transition-transform duration-500 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="flex h-full flex-col">
          <div className={`flex flex-shrink-0 items-center justify-between py-[30px] max-w-[90%] mx-auto w-full transition-all duration-300 ${showHeader ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"}`}>
            <Link href="/" onClick={() => setIsOpen(false)}>
              <Image
                src={logo}
                alt="logo"
                className="h-fit w-[125px] transition hover:opacity-80"
                loading="lazy"
              />
            </Link>

            <div className="flex items-center gap-4">
              <Dropdown
                theme={theme}
                label={
                  <div className="flex items-center gap-2 cursor-pointer">
                    <Image
                      src={router.locale === "en" ? EngFlag : AzeFlag}
                      alt={`${router.locale}-flag`}
                      className="rounded-sm"
                      width={32}
                      height={32}
                      loading="lazy"
                    />
                    <FaCaretDown className="h-[15px] w-[15px] text-gray-900 transition-transform duration-200 group-aria-expanded:rotate-180" />
                  </div>
                }
                className="!left-[-10px] !top-[-5px]"
                inline
              >
                <Dropdown.Item className="hover:bg-[#F3F4F6] px-2 py-1 flex items-center justify-center" onClick={changeLanguage("az")}>
                  <Image src={AzeFlag} alt="aze-flag" width={32} height={32} loading="lazy" />
                </Dropdown.Item>
                <Dropdown.Item className="hover:bg-[#F3F4F6] px-2 py-1 flex items-center justify-center" onClick={changeLanguage("en")}>
                  <Image src={EngFlag} alt="en-flag" width={32} height={32} loading="lazy" />
                </Dropdown.Item>
              </Dropdown>

              <button
                onClick={() => setIsOpen(false)} className="cursor-pointer flex items-center justify-center" aria-label="Close menu">
                <IoClose className="text-[30px] text-gray-900" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="flex flex-col overflow-y-auto px-4 gap-0" onScroll={handleDrawerScroll}>
            <div className="w-full">
              <button
                onClick={() => setAcademyOpen(!academyOpen)}
                className="flex flex-col w-full py-3 cursor-pointer"
              >
                <div className="flex items-center justify-between w-full">
                  <span className={`text-[16px] px-4 ${activeDropdown === "academy" ? "text-main-pink" : "text-black"}`}>{t("navlinks.academy")}</span>
                  <FaCaretDown className={`h-[15px] w-[15px] text-gray-900 mr-[15px] transition-transform duration-200 ${academyOpen ? "rotate-180" : ""}`} />
                </div>
                {activeDropdown === "academy" && !academyOpen && <div className="h-[1px] w-full bg-main-pink mt-2 max-w-[95%] mx-auto" />}
              </button>
              {academyOpen && (
                <div className="flex flex-col">
                  <Link href="/about-us" className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.about-us")}
                    {activeChildLink === "about-us" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                  <Link href="/methodology" className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.methodology")}
                    {activeChildLink === "methodology" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                  <Link href="/branches" className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.branches")}
                    {activeChildLink === "branches" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                </div>
              )}
            </div>

            <div className="w-full">
              <button
                onClick={() => setProgramsOpen(!programsOpen)}
                className="flex flex-col w-full py-3 cursor-pointer"
              >
                <div className="flex items-center justify-between w-full">
                  <span className={`text-[16px] px-4 ${activeDropdown === "programs" ? "text-main-pink" : "text-black"}`}>{t("navlinks.programs")}</span>
                  <FaCaretDown className={`h-[15px] w-[15px] text-gray-900 mr-[15px] transition-transform duration-200 ${programsOpen ? "rotate-180" : ""}`} />
                </div>
                {activeDropdown === "programs" && !programsOpen && <div className="h-[1px] w-full bg-main-pink mt-2 max-w-[95%] mx-auto" />}
              </button>
              {programsOpen && (
                <div className="flex flex-col">
                  <Link href="/programs" onClick={() => setIsOpen(false)} className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.all-programs")}
                    {activeChildLink === "programs" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                  <Link href="/programs/front-end" onClick={() => setIsOpen(false)} className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.programs-frontend")}
                    {activeChildLink === "programs-frontend" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                  <Link href="/programs/back-end" onClick={() => setIsOpen(false)} className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.programs-backend")}
                    {activeChildLink === "programs-backend" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                  <Link href="/programs/full-stack" onClick={() => setIsOpen(false)} className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.programs-fullstack")}
                    {activeChildLink === "programs-fullstack" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                  <Link href="/programs/software-engineering" onClick={() => setIsOpen(false)} className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.programs-softwareengineering")}
                    {activeChildLink === "programs-softwareengineering" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                  <Link href="/programs/summer-bootcamp" onClick={() => setIsOpen(false)} className="text-left px-4 pb-4 text-sm text-black flex flex-col hover:bg-[#F3F4F6]">
                    {t("navlinks.programs-summerbootcamp")}
                    {activeChildLink === "programs-summerbootcamp" && <div className="h-[1px] w-full bg-main-pink mt-2" />}
                  </Link>
                </div>
              )}
            </div>

            <Link href="/blogs" onClick={() => setIsOpen(false)} className={`flex flex-col py-3 ${router.pathname === "/blogs" ? "text-main-pink" : "text-black"}`}>
              <span className="text-[16px] px-4">{t("navlinks.blogs")}</span>
              {router.pathname === "/blogs" && <div className="h-[1px] w-full bg-main-pink mt-2 max-w-[95%] mx-auto" />}
            </Link>

            <Link href="/contact-us" onClick={() => setIsOpen(false)} className={`flex flex-col py-3 ${router.pathname === "/contact-us" ? "text-main-pink" : "text-black"}`}>
              <span className="text-[16px] px-4">{t("navlinks.contact-us")}</span>
              {router.pathname === "/contact-us" && <div className="h-[1px] w-full bg-main-pink mt-2 max-w-[95%] mx-auto" />}
            </Link>
          </div>

          <div className="pl-8 pr-4 pb-8 pt-4">
            <Link href="/apply" onClick={() => { setIsOpen(false); trackApplyButton("drawer"); }}>
              <button className="w-full md:w-[300px] h-[48px] md:h-[56px] rounded-full bg-main-pink text-white text-[16px] font-[500] hover:opacity-90 transition hover:text-main-pink hover:bg-white border border-main-pink">
                {t("apply-btn")}
              </button>
            </Link>
          </div>
        </div>
      </section>
      <section className="h-full w-screen cursor-pointer" onClick={() => setIsOpen(false)} />
    </main>
  );
}
